import { graphqlRequest } from "./client";
import { CHECK_FIELDS, GATE_FIELDS, type CheckItem, type StepGate } from "./types";

/* ---------- Step 5: Run It ---------- */

export interface RunItData {
  gig: { client: string; source: string; what: string; value?: number | null };
  checklist: string[];
  reputation: { reviewMsg: string; reviewSent: boolean; retainer: string; retainerSent: boolean };
}

export interface RunItView {
  step: RunItData;
  sources: string[];
  checks: CheckItem[];
  gate: StepGate;
  done: boolean;
}

const RUN_FIELDS = `step { gig { client source what value } checklist reputation { reviewMsg reviewSent retainer retainerSent } }
  sources checks { ${CHECK_FIELDS} } ${GATE_FIELDS} done`;

/** Step 5: Run It. */
export async function fetchRunIt(): Promise<RunItView> {
  return (await graphqlRequest<{ getRunIt: RunItView }>(`{ getRunIt { ${RUN_FIELDS} } }`)).getRunIt;
}

/** Save Step 5 progress. */
export async function saveRunIt(input: RunItData): Promise<RunItView> {
  return (
    await graphqlRequest<{ saveRunIt: RunItView }>(
      `mutation($input: SaveRunItInput!) { saveRunIt(input: $input) { ${RUN_FIELDS} } }`,
      { variables: { input } },
    )
  ).saveRunIt;
}

/** Complete Step 5. */
export async function completeRunIt(): Promise<RunItView> {
  return (
    await graphqlRequest<{ completeRunIt: RunItView }>(
      `mutation { completeRunIt { ${RUN_FIELDS} } }`,
    )
  ).completeRunIt;
}

/* ---------- Step 6: Keep It and Grow ---------- */

export interface Budget {
  m1?: number | null;
  m2?: number | null;
  m3?: number | null;
  ess?: number | null;
  needs?: number | null;
  wants?: number | null;
  save?: number | null;
  reviewDay?: string | null;
}

export interface GrowthPlan {
  goal?: number | null;
  goalBy?: string | null;
  tool?: string | null;
  target?: number | null;
  a1?: string | null;
  a2?: string | null;
  a3?: string | null;
  skill?: string | null;
}

export interface GrowView {
  step: { budget: Budget; plan: GrowthPlan };
  calc?: {
    low: number;
    avg: number;
    needsAmt: number;
    wantsAmt: number;
    saveAmt: number;
    buffer: number;
    months?: number | null;
    sum: number;
  } | null;
  warnings: string[];
  reviewDays: string[];
  trackingTools: string[];
  gate: StepGate;
  done: boolean;
}

const GROW_FIELDS = `step { budget { m1 m2 m3 ess needs wants save reviewDay } plan { goal goalBy tool target a1 a2 a3 skill } }
  calc { low avg needsAmt wantsAmt saveAmt buffer months sum } warnings reviewDays trackingTools ${GATE_FIELDS} done`;

/** Step 6: Keep It and Grow. */
export async function fetchGrow(): Promise<GrowView> {
  return (await graphqlRequest<{ getGrow: GrowView }>(`{ getGrow { ${GROW_FIELDS} } }`)).getGrow;
}

/** Save the budget and growth plan. */
export async function saveGrow(budget: Budget, plan: GrowthPlan): Promise<GrowView> {
  return (
    await graphqlRequest<{ saveGrow: GrowView }>(
      `mutation($input: SaveGrowInput!) { saveGrow(input: $input) { ${GROW_FIELDS} } }`,
      { variables: { input: { budget, plan } } },
    )
  ).saveGrow;
}

/** Complete Step 6 and the pathway. */
export async function completeGrow(): Promise<GrowView> {
  return (
    await graphqlRequest<{ completeGrow: GrowView }>(`mutation { completeGrow { ${GROW_FIELDS} } }`)
  ).completeGrow;
}

/* ---------- Grow Library ---------- */

export interface LibraryModule {
  key: string;
  group: string;
  title: string;
  source: string;
  summary: string;
  topics: string[];
  action: string;
  done: boolean;
}

export interface GrowLibrary {
  unlocked: boolean;
  modules: LibraryModule[];
}

const LIBRARY_FIELDS = "unlocked modules { key group title source summary topics action done }";

/** The optional Grow Library. */
export async function fetchGrowLibrary(): Promise<GrowLibrary> {
  return (
    await graphqlRequest<{ getGrowLibrary: GrowLibrary }>(
      `{ getGrowLibrary { ${LIBRARY_FIELDS} } }`,
    )
  ).getGrowLibrary;
}

/** Mark a module read or unread. */
export async function toggleLibraryModule(key: string): Promise<GrowLibrary> {
  return (
    await graphqlRequest<{ toggleLibraryModule: GrowLibrary }>(
      `mutation($key: String!) { toggleLibraryModule(key: $key) { ${LIBRARY_FIELDS} } }`,
      { variables: { key } },
    )
  ).toggleLibraryModule;
}
